import { Site } from "./site.model"
import type { ClosedownAsset, ClosedownJobDetail, ClosedownPayload } from './closedown-detail.model';

export interface JobEvent {
    id : string,
    jobId : string,
    signInSignOutId : string | null,
    workOrderId : string | null,
    assetTag : string | null,
    calloutReferenceNumber : string | null,
    parentCalloutReferenceNumber : string | null,
    bookingStatus : string | null,
    equipmentStatus : string | null,
    rootCauseID : string | null,
    bookedOnUtc : Date | null,
    onsiteUtc : Date | null,
    timeSpentMinutes : number | null,
    travelTime : number | null,
    workCompleted : string | null,
    reasonForReturning : string | null,
    returningDateUTC : Date | null,
    assetConditionOnArrival : string | null,
    assetConditionOnDeparture : string | null,
    thirdPartyUsed : string | null,
    thirdPartyDescription : string | null,
    wereConsumablesUsed : string | null,
    consumablesDescription : string | null
}

export interface Job {
    id : string,
    siteId : string,
    brandId?: string,
    jobStatusId : string,
    referenceNumber : string,
    source : string,
    jobType : string,
    faultServiceName : string,
    departmentName : string,
    jobStatusName : string, 
    companyReferenceNumber : string, 
    disciplineReferenceId?: string,
    faultServiceReferenceId?: string,
    incidentTypeReferenceId?: string,
    allocatedUserEmail : string,
    allocatedTechnicianName? : string | null,
    allocatedContactNumber? : string | null,
    allocatedReferenceId : string,
    eta : Date,
    notes : string,
    createdBy : string,
    createdAt : Date,
    updatedBy : string,
    updatedAt : Date,
    reasonForVisitCheck: boolean,
    calloutReferenceNumber : string,
    site : Site,
    version: number,
    latestJobEvent: JobEvent
    subSite : string,
    allocateCompanyId : string,
    allocatedCompanyName : string | null,
    companyId : string
}

export interface PendingApprovalJob {
    id: string;
    siteId: string;
    jobType: string | null;
    referenceNumber: string | null;
    faultServiceName: string | null;
    notes: string | null;
    allocatedUserEmail: string | null;
    workOrderNumber: string | null;
    site: {
        name: string | null;
        brand: { name: string | null } | null;
    } | null;
    latestJobEvent: {
        bookedOnUtc: string | null;
    } | null;
}

export interface UnallocatedJobDto {
    id: string;
    siteId: string;
    companyId: string | null;
    referenceNumber: string | null;
    faultServiceName: string | null;
    notes: string | null;
    workOrderNumber: string | null;
    site: {
        name: string | null;
        brand: { name: string | null } | null;
    } | null;
    latestJobEvent: {
        bookedOnUtc: string | null;
    } | null;
}

// Projection of JobDTO returned by Jobs/GetAllocatedJobById.
export interface AllocatedJobDto {
    id: string;
    siteId: string;
    referenceNumber: string | null;
    jobType: string | null;
    jobStatusId: string;
    jobStatusName: string | null;
    faultServiceName: string | null;
    departmentName: string | null;
    notes: string | null;
    companyId: string | null;
    allocateCompanyId: string | null;
    allocatedUserEmail: string | null;
    calloutReferenceNumber: string;
    disciplineReferenceId: string | null;
    faultServiceReferenceId: string | null;
    incidentTypeReferenceId: string | null;
    latestJobEvent: JobEvent | null;
    jobEvents?: JobEvent[] | null;
    jobEventClosedown: ClosedownPayload | null;
    asset?: ClosedownAsset;
    site: {
        id: string;
        brandId: string;
        name: string | null;
        brand: {
            id: string | null;
            referenceNumber: string | null;
        } | null;
    } | null;
}

export function mapAllocatedJob(dto: AllocatedJobDto): ClosedownJobDetail {
    if (!dto.id || !dto.siteId || !dto.referenceNumber || !dto.jobType ||
        !dto.companyId || !dto.latestJobEvent?.id || !dto.site?.brand?.id ||
        !dto.site.brand.referenceNumber) {
        throw new Error('Allocated job response is missing required closedown details.');
    }

    return {
        id: dto.id,
        siteId: dto.siteId,
        referenceNumber: dto.referenceNumber,
        jobType: dto.jobType,
        jobStatusId: dto.jobStatusId,
        jobStatusName: dto.jobStatusName ?? '',
        faultServiceName: dto.faultServiceName ?? '',
        departmentName: dto.departmentName ?? '',
        notes: dto.notes ?? '',
        companyId: dto.companyId,
        ...(dto.allocateCompanyId ? { allocateCompanyId: dto.allocateCompanyId } : {}),
        allocatedUserEmail: dto.allocatedUserEmail ?? '',
        calloutReferenceNumber: dto.calloutReferenceNumber,
        disciplineReferenceId: dto.disciplineReferenceId ?? '',
        faultServiceReferenceId: dto.faultServiceReferenceId ?? '',
        incidentTypeReferenceId: dto.incidentTypeReferenceId ?? '',
        latestJobEvent: dto.latestJobEvent,
        jobEvents: dto.jobEvents ?? null,
        jobEventClosedown: dto.jobEventClosedown,
        ...(dto.asset ? { asset: dto.asset } : {}),
        brandId: dto.site.brandId,
        site: {
            id: dto.site.id,
            name: dto.site.name ?? '',
            brandId: dto.site.brandId,
            brand: {
                id: dto.site.brand.id,
                referenceNumber: dto.site.brand.referenceNumber
            }
        }
    };
}

export interface PickupJob {
    jobId : string,
    resourceId : string,
    subContractorEmail : string,
    companyName : string,
    technicianName : string,
    mobileNumber : string,
    jobType : string
}

export interface UnallocateJob {
    jobId : string,
    resourceId : string,
    calloutReferenceNumber : string,
    jobType : string
}

export interface JobAllocation {
    jobId : string,
    allocatedUserEmail : string,
    allocationType: number,
    companyId : string
}

export interface DashboardSummary {
    openJobs: number,
    pendingApprovalJobs: number,
    openPpms: number
}

export interface OpenJobsSummarySite {
    id: string,
    brandId: string,
    name: string | null,
    code: string | null,
    isActive: boolean
}

export interface OpenJobsSummaryBrand {
    id: string | null,
    code: string | null,
    name: string | null,
    referenceNumber: string | null,
    source: string | null,
    isActive: boolean
}

export interface OpenJobsSummary {
    site: OpenJobsSummarySite | null,
    brand: OpenJobsSummaryBrand | null,
    new: number,
    onSite: number,
    returning: number,
    awaitingParts: number,
    ppm: number
}
